import { BookableStorageLocation } from '@luggagehero/shared/interfaces';

/**
 * How the traveler apps name a shop, in one place.
 *
 * A traveler who finds LuggageHero on Google sees the listing title
 * `LuggageHero Luggage Storage Camden`, then lands in the app and should recognise the same
 * place. So the apps render `LuggageHero <fragment>`, and this resolves the fragment.
 *
 * The chain was previously written out in several places and the copies had drifted apart: only
 * one applied a distance limit to the POI, and one had no last resort at all, so a shop with
 * neither POI nor city rendered "LuggageHero undefined". They now share this.
 */

export interface ShopTitleLocationOptions {
  /**
   * Reject the nearest POI when it is further away than this, in metres, and fall through to the
   * city. Omit to accept the nearest POI at any distance.
   *
   * Optional rather than a shared constant because the call sites legitimately differ: a search
   * result names a landmark only if it is one the traveler could walk to, while the details page
   * for a shop the traveler has already chosen names its nearest landmark regardless. Unifying the
   * two is a product decision, not a refactor, so this preserves both.
   */
  maxPoiDistance?: number;
}

/**
 * The location fragment to show after the brand, or `null` when the shop offers nothing usable.
 *
 * Order: the curated `titleLocation` (what the Google listing actually says) → the nearest POI →
 * the city. `null` rather than a default string, because the right last resort differs per caller
 * — a translated "Luggage storage", the shop's own name on a walk-in — and this library cannot
 * translate.
 */
export function getShopTitleLocation(
  shop: Pick<BookableStorageLocation, 'titleLocation' | 'poiList' | 'address'> | null | undefined,
  options: ShopTitleLocationOptions = {},
): string | null {
  if (!shop) {
    return null;
  }

  // Trimmed, and an all-whitespace value treated as absent: the API declares it as a plain
  // optional string, so a fragment of spaces would otherwise render "LuggageHero " with a
  // trailing space rather than falling back to the POI.
  const titleLocation = shop.titleLocation?.trim();
  if (titleLocation) {
    return titleLocation;
  }

  const nearestPoi = shop.poiList?.[0];
  if (nearestPoi && isWithinDistance(nearestPoi.distance, options.maxPoiDistance)) {
    const poiName = nearestPoi.poiName?.trim();
    if (poiName) {
      return poiName;
    }
  }

  return shop.address?.city?.trim() || null;
}

function isWithinDistance(distance: number | undefined, maxPoiDistance: number | undefined): boolean {
  if (maxPoiDistance === undefined) {
    return true;
  }

  // A POI with no distance is not "at distance 0" — it is unmeasured, and the caller asked for a
  // limit. Treat it the way the shop card always has: out of range, fall through to the city.
  if (typeof distance !== 'number' || !Number.isFinite(distance)) {
    return false;
  }

  return distance >= 0 && Math.round(distance) <= maxPoiDistance;
}
